import React, { useState } from 'react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import AddToHomeScreenContainer from '@/containers/AddToHomeScreenContainer';
import { GoogleTagManagerScript } from '@/containers/AnalyticsContainer/GoogleTagManager';
import { MetricaScript } from '@/containers/AnalyticsContainer/Metrica';
import { UTMContainer } from '@/containers/AnalyticsContainer/UTMContainer';
import CacheContainer from '@/containers/CacheContainer';
import ChatContainer from '@/containers/ChatContainer';
import EContractContainer from '@/containers/EContractContainer';
import FestivalInviteLinkContainer from '@/containers/FestivalInviteLinkContainer';
import FirebaseContainer from '@/containers/FirebaseContainer';
import FormbricksContainer from '@/containers/FormbricksContainer';
import LayoutContainer from '@/containers/LayoutContainer';
import { PrepareEncryptionContainer } from '@/containers/PrepareEncryptionContainer';
import UnleashContainer from '@/containers/UnleashContainer';
import UpdateContainer from '@/containers/UpdateContainer';
import UserStatusContainer from '@/containers/UserStatusContainer';
import AuthSessionProvider from '@/modules/auth/containers/AuthSessionProvider';
import PasscodeGuardContainer from '@/modules/auth/containers/PasscodeGuardContainer';
import ErrorsContainer from '@/modules/error/containers/ErrorsContainer';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import 'swiper/css/pagination';
import { defaultQueryOptions } from '@/services/utils';
import { BottomSheetContainer } from '@/components/BottomSheet';
import { ToastContainer } from '@/components/Toast';
import { QueryClientConfig } from 'react-query/types/core/types';
import '../styles/globals.css';
import 'swiper/css';
import '../polyfills';

const keywords =
  'کارگزاری مفید، سرمایه گذاری هوشمند، خرید و فروش صندوق، مدیریت خدمات مفیدکارت، سبد سهام، پس‌انداز خودکار، آموزش رایگان بورس، مشاوره رایگان سرمایه گذاری، مدیریت مالی';

const QueryClientOptions: QueryClientConfig = {
  defaultOptions: { queries: defaultQueryOptions },
};

const MyApp = ({
  Component,
  pageProps: { dehydratedState, ...pageProps },
}: AppProps<{ dehydratedState: object }> & {
  Component: PageComponent;
}) => {
  /*https://react-query.tanstack.com/guides/ssr#using-hydration*/
  const [queryClient] = useState(() => new QueryClient(QueryClientOptions));

  return (
    <>
      <Head>
        <title>
          {[Component?.title, 'اپلیکیشن مفید | Mofid']
            .filter(Boolean)
            .join(' - ')}
        </title>
        <meta
          content="minimum-scale=1, maximum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no"
          name="viewport"
        />
        <meta content={keywords} name="keywords" />
      </Head>
      <>
        <GoogleTagManagerScript />
        <MetricaScript />
        <CacheContainer />
        <AddToHomeScreenContainer />
        <ToastContainer />
        <UTMContainer />
      </>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedState}>
          <ReactQueryDevtools panelProps={{ style: { direction: 'ltr' } }} />
          <ErrorsContainer>
            <AuthSessionProvider>
              <UserStatusContainer
                showBottomSheet={Component?.showUserStatusBottomSheet ?? true}
              >
                <UnleashContainer>
                  <PasscodeGuardContainer>
                    <EContractContainer>
                      <LayoutContainer Component={Component}>
                        <Component {...pageProps} />
                      </LayoutContainer>
                    </EContractContainer>
                    <ChatContainer />
                  </PasscodeGuardContainer>
                  <BottomSheetContainer />
                  <FirebaseContainer />
                  <UpdateContainer />
                  <PrepareEncryptionContainer />
                  <FestivalInviteLinkContainer />
                  <FormbricksContainer />
                </UnleashContainer>
              </UserStatusContainer>
            </AuthSessionProvider>
          </ErrorsContainer>
        </Hydrate>
      </QueryClientProvider>
    </>
  );
};

export default MyApp;
